import { useState } from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { FileText, Users, Layers, Building2, LogOut, KeyRound, ShieldCheck } from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const navItems = [
  { to: "/decisions", label: "القرارات", icon: FileText, testid: "nav-decisions" },
  { to: "/employees", label: "الموظفون", icon: Users, testid: "nav-employees" },
  { to: "/categories", label: "الفئات", icon: Layers, testid: "nav-categories" },
  { to: "/entity", label: "بيانات الجهة", icon: Building2, testid: "nav-entity" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-40 bg-white border-b border-border shadow-sm">
        <div className="flex items-center justify-between gap-4 px-4 md:px-6 h-[104px]">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="سِجِل للأرشفة الإلكترونية" className="h-[92px] w-auto object-contain" data-testid="header-logo" />
          </div>
          <div className="flex items-center gap-1 md:gap-2 flex-wrap justify-end">
            {user?.role === "admin" && (
              <Button variant="ghost" asChild className="text-primary hover:text-secondary" data-testid="admin-panel-link">
                <Link to="/admin">
                  <ShieldCheck className="h-4 w-4 ms-1" />
                  لوحة التحكم
                </Link>
              </Button>
            )}
            <Button variant="ghost" className="text-primary hover:text-secondary" onClick={() => setPwOpen(true)} data-testid="change-password-btn">
              <KeyRound className="h-4 w-4 ms-1" />
              تغيير كلمة المرور
            </Button>
            <span className="text-sm text-muted-foreground hidden md:inline-block px-2" data-testid="current-user-name">
              {user?.name}
            </span>
            <Button variant="outline" onClick={handleLogout} className="border-primary/30 text-primary hover:bg-primary hover:text-white transition-colors" data-testid="logout-btn">
              <LogOut className="h-4 w-4 ms-1" />
              تسجيل الخروج
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 items-stretch">
        <aside className="w-56 md:w-64 bg-primary text-white shrink-0" data-testid="dashboard-sidebar">
          <nav className="p-4 space-y-2 sticky top-[104px]">
            {navItems.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-colors duration-200 ${
                    isActive ? "bg-secondary text-white shadow-md" : "text-white/80 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon className="h-5 w-5" />
                {label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 p-4 md:p-8 min-w-0">
          <Outlet />
        </main>
      </div>

      <footer className="border-t border-border bg-white py-4 text-center text-sm text-muted-foreground" data-testid="app-footer">
        فكرة وتصميم الأستاذة/ جبرة الطويرقي
      </footer>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
